<script>
    import { getBookAccordionDeps } from './context.js';

    const { access, features } = getBookAccordionDeps();
    const canAccessTeacherOnlyMaterial = access?.canAccessTeacherOnlyMaterial ?? (() => false);
    const isContentVisibilityEnabled = features?.isContentVisibilityEnabled ?? (() => false);

    /**
     * Shared "hidden / visible from students" indicator. Used by both
     * BookAccordianItem (library sidebar rows) and ResourceHolder
     * (resource cards inside ResourceHub) so the look stays consistent.
     *
     * Renders nothing for non-privileged users — students should never
     * see either state of the badge.
     *
     * When `onclick` is provided, the badge becomes interactive (acts as
     * a toggle button). Renders as a role="button" element rather than a
     * real <button> so it can be safely nested inside other buttons (e.g.
     * the .row resource-item button) without producing invalid HTML.
     *
     * @typedef {Object} Props
     * @property {boolean} [hidden]
     * @property {(() => void) | null} [onclick]
     * @property {boolean} [pending]
     */
    let { hidden = false, onclick = null, pending = false } = $props();

    let canSee = $derived(isContentVisibilityEnabled() && canAccessTeacherOnlyMaterial());
    let interactive = $derived(typeof onclick === 'function' && !pending);
    let label = $derived.by(() => {
        if (interactive) {
            return hidden
                ? 'Hidden from students — click to show'
                : 'Visible to students — click to hide';
        }
        return hidden
            ? 'Hidden from students in this class'
            : 'Visible to students in this class';
    });

    function handleClick(e) {
        if (!interactive) return;
        e.preventDefault();
        e.stopPropagation();
        onclick?.();
    }

    function handleKeydown(e) {
        if (!interactive) return;
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            onclick?.();
        }
    }
</script>

{#if canSee}
    <!-- svelte-ignore a11y_no_noninteractive_tabindex -->
    <span
        class="visibility-badge {hidden ? 'visibility-badge--off' : 'visibility-badge--on'} {interactive ? 'visibility-badge--interactive' : ''} {pending ? 'visibility-badge--pending' : ''}"
        title={label}
        aria-label={label}
        role={interactive ? 'button' : null}
        tabindex={interactive ? 0 : null}
        onclick={handleClick}
        onkeydown={handleKeydown}
        aria-disabled={pending ? 'true' : null}
    >
        <i class="material-icons" aria-hidden="true">
            {hidden ? 'visibility_off' : 'visibility'}
        </i>
    </span>
{/if}

<style>
    .visibility-badge {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: 0.15rem;
        border-radius: 0.25rem;
        line-height: 1;
        flex-shrink: 0;
        text-decoration: none !important;
    }

    .visibility-badge--off {
        background-color: rgba(255, 99, 99, 0.85) !important;
        color: #fff !important;
    }

    .visibility-badge--on {
        background-color: rgba(255, 255, 255, 0.12) !important;
        color: rgba(255, 255, 255, 0.7) !important;
    }

    .visibility-badge i {
        font-size: 0.95rem;
        color: inherit !important;
        margin-right: 0 !important;
        text-decoration: none !important;
    }

    .visibility-badge--interactive {
        cursor: pointer;
        transition: filter 0.12s ease-in-out, transform 0.12s ease-in-out;
    }

    .visibility-badge--interactive:hover {
        filter: brightness(1.15);
    }

    .visibility-badge--interactive:active {
        transform: scale(0.94);
    }

    .visibility-badge--interactive:focus-visible {
        outline: 2px solid #4ac6ff;
        outline-offset: 2px;
    }

    .visibility-badge--pending {
        cursor: progress;
        opacity: 0.6;
    }
</style>
